import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Reusable announcement modal that shows once per announcement ID.
 * Uses localStorage to track which announcements have been seen.
 *
 * @param {Object} props
 * @param {string} props.announcementId - Unique ID for this announcement (e.g., 'ios-app-launch-2024')
 * @param {React.ReactNode} props.icon - Icon to display at the top
 * @param {string} props.title - Modal title
 * @param {string} props.subtitle - Modal subtitle/description
 * @param {string} props.primaryButtonText - Text for primary CTA button
 * @param {string} props.secondaryButtonText - Text for secondary/dismiss button
 * @param {function} props.onPrimaryClick - Handler for primary button click
 * @param {function} props.onClose - Handler for modal close
 * @param {boolean} props.forceShow - Force show the modal (bypasses localStorage check)
 */
export function AnnouncementModal({
  announcementId,
  icon,
  title,
  subtitle,
  primaryButtonText = 'Learn More',
  secondaryButtonText = 'Maybe Later',
  onPrimaryClick,
  onClose,
  forceShow = false,
}) {
  const [isVisible, setIsVisible] = useState(false);

  const storageKey = `announcement_seen_${announcementId}`;

  useEffect(() => {
    // Check if this announcement has been seen before
    const hasSeenAnnouncement = localStorage.getItem(storageKey);

    if (forceShow || !hasSeenAnnouncement) {
      setIsVisible(true);
    }
  }, [storageKey, forceShow]);

  const handleClose = () => {
    // Mark as seen
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
    onClose?.();
  };

  const handlePrimaryClick = () => {
    // Mark as seen
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
    onPrimaryClick?.();
  };

  if (!isVisible) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'var(--overlay-bg)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '24px',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-overlay)',
          padding: 'var(--space-6)',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        {icon && (
          <div style={{
            width: '72px',
            height: '72px',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </div>
        )}

        {/* Title */}
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: 'var(--text-primary)',
          marginBottom: '12px',
          letterSpacing: '-0.02em',
        }}>
          {title}
        </h2>

        {/* Subtitle */}
        <p style={{
          fontSize: '15px',
          color: 'var(--text-secondary)',
          lineHeight: '1.6',
          marginBottom: '28px',
        }}>
          {subtitle}
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={handlePrimaryClick}
            style={{
              width: '100%',
              padding: '14px 24px',
              borderRadius: 'var(--radius-panel)',
              background: 'var(--btn-primary-bg)',
              color: 'var(--btn-primary-text)',
              fontSize: '16px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {primaryButtonText}
          </button>

          <button
            onClick={handleClose}
            style={{
              width: '100%',
              padding: '14px 24px',
              borderRadius: 'var(--radius-panel)',
              background: 'transparent',
              color: 'var(--text-tertiary)',
              fontSize: '15px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              transition: 'color 0.15s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = 'var(--text-tertiary)';
            }}
          >
            {secondaryButtonText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Pre-configured iOS App Store announcement modal
 */
export function IOSAppAnnouncementModal({ onDownload, onClose }) {
  // iPhone mockup icon with Graspd sprout
  const phoneIcon = (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
      {/* Phone outline */}
      <rect x="16" y="4" width="32" height="56" rx="6" stroke="var(--text-secondary)" strokeWidth="2" fill="none" />
      {/* Screen */}
      <rect x="19" y="10" width="26" height="44" rx="2" fill="var(--glass-bg-medium)" />
      {/* Notch/dynamic island */}
      <rect x="26" y="12" width="12" height="3" rx="1.5" fill="var(--text-tertiary)" />
      {/* Sprout icon on screen */}
      <g transform="translate(32, 34)" stroke="var(--accent-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M0 10v-6"/>
        <path d="M0 4c-2.5 0-5-1.5-5-5 3.5 0 5 1.5 5 5z"/>
        <path d="M0 1c2.5 0 5-1.5 5-5-3.5 0-5 1.5-5 5z"/>
      </g>
      {/* Home indicator */}
      <rect x="26" y="50" width="12" height="2" rx="1" fill="var(--text-tertiary)" />
    </svg>
  );

  return (
    <AnnouncementModal
      announcementId="ios-app-launch-v1"
      icon={phoneIcon}
      title="Now on the App Store!"
      subtitle="Take your learning anywhere. Download Graspd for iOS and learn on the go with native mobile experience."
      primaryButtonText="Download for iOS"
      secondaryButtonText="Continue in Browser"
      onPrimaryClick={onDownload}
      onClose={onClose}
    />
  );
}

export default AnnouncementModal;
